import { useState, useEffect } from "react";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import { ShoppingCart, LogOut } from "lucide-react";
import { useInfinityCustomer } from "../../context/InfinityCustomerContext";
import { getImageUrl } from "../../utils/imageUtils";
import InfinityCartSheet from "./InfinityCartSheet";

/**
 * Themed shell for customer.infinitytouch.app. Sticky header (TR-9 #4). Nav:
 * Home → Orders → Fingerprint Jewelry (IT) → All Products → Cart → Logout.
 * The cart is a right-hand slide-out drawer.
 *
 * "All Products" (IT + edisplay together) is deliberately hidden until the family
 * has placed an order and taken up the offer on the thank-you screen (TR-22) — and
 * only ever shows for funeral homes that actually sell edisplay.
 */
export default function InfinityLayout() {
  const { theme, cartCount, isAuthed, logout, setCartOpen, basePath, hasEdisplay, showAllProducts } = useInfinityCustomer();
  const navigate = useNavigate();
  const location = useLocation();
  const go = (p) => navigate(`${basePath}${p}`);
  const onLogin = location.pathname.endsWith("/login") || location.pathname === `${basePath}` || location.pathname === `${basePath}/`;
  const [logoErr, setLogoErr] = useState(false);

  useEffect(() => {
    setLogoErr(false);
  }, [theme?.logo_url]);

  // Matches the Infinity Touch website header. Keep in step with DEFAULT_THEME on the
  // server, or the header flashes the old colour before the config request lands.
  const headerBg = theme.header_bg_color || "#1f1f1f";
  const font = theme.font_family || "Poppins, sans-serif";

  const navBtn = (label, onClick) => (
    <button onClick={onClick} className="text-sm font-medium text-white/90 hover:text-white transition-colors">
      {label}
    </button>
  );

  return (
    <div className={`min-h-screen flex flex-col ${onLogin ? 'h-screen overflow-hidden' : ''}`} style={{ fontFamily: font, background: theme.background_color || "#F8FAFC" }}>
      <style>{`.it-root, .it-root * { font-family: ${font}; }`}</style>
      
      {!onLogin ? (
        <header style={{ backgroundColor: headerBg }} className="text-white sticky top-0 z-50 shadow-sm">
          <div className="max-w-6xl mx-auto px-4 h-16 flex items-center justify-between">
            <button onClick={() => go(isAuthed ? "/home" : "/login")} className="flex items-center gap-2">
              {theme.logo_url && !logoErr ? (
                <img
                  src={getImageUrl(theme.logo_url)}
                  alt="Infinity Touch"
                  className="h-9 w-auto max-w-[200px] object-contain"
                  onError={() => setLogoErr(true)}
                />
              ) : (
                <span className="text-xl font-bold tracking-tight">Infinity Touch</span>
              )}
            </button>

            {isAuthed ? (
              <nav className="flex items-center gap-6">
                {navBtn("Home", () => go("/home"))}
                {navBtn("Orders", () => go("/home#orders"))}
                {navBtn("Fingerprint Jewelry", () => go("/shop"))}
                {hasEdisplay && showAllProducts ? navBtn("All Products", () => go("/shop?channel=all")) : null}
                {navBtn("Addresses", () => go("/addresses"))}
                <button onClick={() => setCartOpen(true)} className="relative" aria-label="Cart">
                  <ShoppingCart size={20} />
                  {cartCount > 0 ? (
                    <span className="absolute -top-2 -right-2 text-[10px] font-bold rounded-full w-4 h-4 flex items-center justify-center"
                      style={{ backgroundColor: theme.accent_color || "#E0A93C", color: "#1f2937" }}>
                      {cartCount}
                    </span>
                  ) : null}
                </button>
                <button onClick={() => { logout(); go("/login"); }} aria-label="Log out" className="opacity-90 hover:opacity-100 flex items-center gap-1 text-sm">
                  <LogOut size={18} />
                </button>
              </nav>
            ) : null}
          </div>
        </header>
      ) : null}

      <main className={`it-root ${onLogin ? 'flex-1 w-full h-full p-0 m-0' : 'flex-1 max-w-6xl w-full mx-auto px-4 py-6'}`}>
        <Outlet />
      </main>

      {!onLogin ? (
        <footer className="py-4 text-center text-xs text-slate-400">
          © {new Date().getFullYear()} Infinity Touch. All rights reserved.
        </footer>
      ) : null}

      {isAuthed ? <InfinityCartSheet /> : null}
    </div>
  );
}
